// src/pages/LandingPage.jsx
import TrustBadges from '../components/TrustBadges';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import PricingBox from '../components/PricingBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBan, faPhone, faEnvelope, faClock } from '@fortawesome/free-solid-svg-icons';
import FeatureGrid from '../components/FeatureGrid';
import { api } from '../utilities/api';
import { formatPublicPrice, getStartingPricesByProduct } from '../utilities/publicPricing';

function LandingPage() {
  const [publicPrices, setPublicPrices] = useState([]);
  const [pricingStatus, setPricingStatus] = useState('loading');

  // This hero/comparison section calls out Semaglutide and Tirzepatide by
  // name (fixed marketing copy, not in scope to redesign for N products);
  // this just looks each one's starting price up from the generic list.
  const startingPriceFor = (nameFragment) => {
    const match = publicPrices.find(p => String(p.product_name || '').toLowerCase().includes(nameFragment));
    return match ? match.price : null;
  };
  const semaglutidePrice = startingPriceFor('semaglutide');
  const tirzepatidePrice = startingPriceFor('tirzepatide') ?? startingPriceFor('tirz');

  useEffect(() => {
    const video = document.querySelector("video");
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    api.getPublicPriceInfo()
      .then(response => {
        if (!isMounted) return;

        if (response?.success && Array.isArray(response.data)) {
          const prices = getStartingPricesByProduct(response.data);
          setPublicPrices(prices);
          setPricingStatus(prices.length ? 'ready' : 'unavailable');
        } else {
          setPricingStatus('unavailable');
        }
      })
      .catch(() => {
        if (isMounted) setPricingStatus('unavailable');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const teaserPrice = (value) => {
    if (pricingStatus === 'loading') return 'Loading pricing...';
    return value != null ? `${formatPublicPrice(value)}*` : 'Pricing unavailable';
  };

  const planPrice = (value) => {
    if (pricingStatus === 'loading') return 'Loading pricing...';
    return value != null ? `From ${formatPublicPrice(value)}/month` : 'Pricing unavailable';
  };

  return (
    <div>
      {/* Hero Section */}
      <Section
        width="fluid"
        className="min-h-[60vh] flex items-center justify-center text-white relative"
      >
        <div className="absolute inset-0">
          <img 
            alt=""
            className="w-full h-full object-cover object-center md:object-top lg:object-[center_20%]"
            src="/images/RediMD-WM-HeroImage.jpg"
          />
          {/* <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/images/RediMD-WM-HeroVideo-Clean.jpg"
            className="w-full h-full object-cover object-center md:object-top lg:object-[center_20%]"
          >
            <source src="/images/RediMD-WM-HeroVideo-Clean.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video> */}
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          {/* Badge: Since 2008 (Top-Left) */}
          <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold">
            Leading Tele-medicine Since 2008
          </div>
          {/* Trust badges (Top-Right) */}
          <div className="absolute hidden md:block top-4 right-4">
            <TrustBadges height={80} />
          </div>
          {/* Badge: Affordable Care (Bottom-Right) */}
          <div className="absolute bottom-4 right-4 bg-highlight text-primary px-3 py-1 rounded-full text-sm font-semibold">
            Doctor Visits $25
          </div>
        </div>
        <div className="text-center relative z-10">
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.7)' }}
          > 
           Real Weight Loss, Real Doctors, Real Results
          </h1>
          <p
            className="text-lg md:text-xl mb-6"
            style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.6)' }}
          >
            Semaglutide and Tirzepatide with personalized plans — not overpriced memberships. Trusted care since 2008.
          </p>
	          <div className="flex flex-col sm:flex-row gap-3 justify-center">
	            <Link
	              to="/register"
	              className="bg-button text-white px-6 py-3 rounded-md hover:bg-button-light transition duration-200"
	            >
	              Start Your Journey Today
	            </Link>
	            <Link
	              to="/pricing"
	              className="bg-white text-primary px-6 py-3 rounded-md hover:bg-neutral-light transition duration-200 font-semibold"
	            >
	              View Pricing
	            </Link>
	          </div>
          <p className="text-sm text-white mt-4">
            Once you qualify, every doctor visit is just $25.00. Real doctors. No memberships. No surprises.
          </p>
          <div className="md:hidden mt-4 flex justify-center">
            <TrustBadges height={56} />
          </div>
        </div>
      </Section>

      {/* Comparison Section */}
      <Section background="bg-neutral-light" width="fixed" className="py-16">
        <h2 className="text-4xl font-extrabold text-center text-primary mb-8">
          The Competition Hoped You Wouldn’t See This.
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto text-lg">
          Others lock you into overpriced memberships and charge before you're even approved.
          We believe in real care, real value — and earning your trust first, with every doctor visit just $25.00 once you're onboard.
        </p>

        <div className="w-full max-w-6xl mx-auto overflow-x-auto">
        <div className="grid grid-cols-3 min-w-[42rem] bg-white border-2 border-primary-light rounded-lg overflow-hidden">
          {/* Column Headers */}
          <div className="flex flex-col items-center justify-center border-r-2 border-primary-light p-4">
            <h3 className="text-2xl font-bold text-primary">RediMD</h3>
          </div>
          <div className="flex flex-col items-center justify-center p-4">
            <h3 className="text-2xl font-bold text-gray-700">What Matters</h3>
          </div>
          <div className="flex flex-col items-center justify-center border-l-2 border-primary-light p-4">
            <h3 className="text-2xl font-bold text-secondary">Them</h3>
          </div>

          {/* Pricing Rows */}
          <div className="flex flex-col items-center justify-center border-t-2 border-primary-light py-6">
            <p className="font-bold text-primary">{teaserPrice(semaglutidePrice)}</p>
            <p className="font-bold text-primary">{teaserPrice(tirzepatidePrice)}</p>
          </div>
          <div className="flex flex-col items-center justify-center border-t-2 border-primary-light py-6">
            <p className="text-gray-700 text-sm">Low-dose Semaglutide</p>
            <p className="text-gray-700 text-sm">Low-dose Tirzepatide</p>
          </div>
          <div className="flex flex-col items-center justify-center border-t-2 border-primary-light py-6">
            <p className="font-bold text-secondary">$125.00+</p>
            <p className="font-bold text-secondary">$249.00+</p>
          </div>

          {/* Features */}
          {[
            { advantage: "Pay Only After Approval", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "No Memberships, No Subscriptions", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "Online Registration and Refills", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "$25 Virtual Doctor Visits", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "US Compounding Pharmacy", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "Transparent Dose-Based Pricing", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "Trusted Since 2008", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
            { advantage: "PayPal Payment Confidence", redimd: <FontAwesomeIcon icon={faCheck} className="text-green-600 fa-2x" />, them: <FontAwesomeIcon icon={faBan} className="text-red-600 fa-2x" /> },
          ].map((item, index) => (
            <React.Fragment key={index}>
              <div className="flex items-center justify-center border-t border-primary-light py-4 text-sm">
                {item.redimd}
              </div>
              <div className="flex items-center justify-center border-t border-primary-light py-4 text-sm text-center px-2">
                <span className="text-primary text-lg">{item.advantage}</span>
              </div>
              <div className="flex items-center justify-center border-t border-primary-light py-4 text-sm">
                {item.them}
              </div>
            </React.Fragment>
          ))}
        </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8 max-w-2xl mx-auto">
          *Prices vary based on dose. RediMD offers real pay-as-you-go pricing based on your prescription needs — not inflated flat fees.
        </p>
      </Section>

      {/* Features Section */}
      <Section background="bg-secondary" width="fixed" className="py-16">
        <h2 className="text-4xl font-extrabold text-center text-primary mb-12">
          Why RediMD Leaves the Rest Behind
        </h2>

        <FeatureGrid />
        
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureBox
            title="Real Doctors, Real Care"
            description="Work directly with licensed providers — not chatbots."
            icon={<StethoscopeIcon />}
          />
          <FeatureBox
            title="Transparent Pricing"
            description="You’ll always know exactly what you're paying for."
            icon={<CreditCardIcon />}
          />
          <FeatureBox
            title="Trusted Medications"
            description="The same active ingredients you know — without inflated costs."
            icon={<PillBottleIcon />}
          />
          <FeatureBox
            title="A Name You Can Trust"
            description="Serving patients nationwide for over 15 years."
            icon={<ShieldHeartIcon />}
          />
        </div> */}

        <p className="text-center text-gray-700 text-sm mt-8">
          * Bloodwork may be required in rare cases.
        </p>
      </Section>

      {/* Testimonials Section */}
      <Section background="bg-neutral-light" width="fixed" className="py-16" id="testimonials">
        <h2 className="text-3xl font-bold text-center text-primary mb-12">Real Patients. Real Weight Loss. Real Results.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white animate-fadeInUp p-6 rounded-lg shadow-md text-center">
            <p className="text-lg italic mb-4">“I lost 52 pounds in just 5 months. RediMD made it simple, affordable, and totally doable.”</p>
            <p className="font-bold text-primary">Eddie S.</p>
            <p className="text-sm text-neutral-dark">Lost 52 lbs in 5 months</p>
          </div>
          <div className="bg-white animate-fadeInUp p-6 rounded-lg shadow-md text-center">
            <p className="text-lg italic mb-4">“The support was amazing. I finally feel like myself again — and it cost way less than I expected.”</p>
            <p className="font-bold text-primary">Lisa K.</p>
            <p className="text-sm text-neutral-dark">Lost 30 lbs</p>
          </div>
          <div className="bg-white animate-fadeInUp p-6 rounded-lg shadow-md text-center">
            <p className="text-lg italic mb-4">“I never thought telehealth weight loss could work this well. RediMD gave me the tools I needed to succeed.”</p>
            <p className="font-bold text-primary">Mark S.</p>
            <p className="text-sm text-neutral-dark">Lost 25 lbs</p>
          </div>
          <div className="bg-white animate-fadeInUp p-6 rounded-lg shadow-md text-center">
            <p className="text-lg italic mb-4">“I paid more for copays before. Now I’m saving money and hitting my weight loss goals.”</p>
            <p className="font-bold text-primary">Jennifer P.</p>
            <p className="text-sm text-neutral-dark">Lost 40 lbs</p>
          </div>
        </div>
      </Section>


      {/* New Section: Semaglutide vs. Tirzepatide */}
      {/* Semaglutide vs Tirzepatide Section */}
      <Section background="bg-highlight-light" width="fixed" className="py-16">
        <h2 className="text-3xl font-bold text-center text-primary mb-12">
          Two Proven Paths to a New You. Which Will You Choose?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="bg-white p-6 rounded-lg shadow-lg animate-fadeInUp">
            <h3 className="text-xl font-semibold text-primary mb-0">Semaglutide</h3>
            <p className="text-primary-dark mb-4">The same active ingredient as Ozempic/Wegovy</p>
            <p className="text-neutral-dark mb-4">
              Semaglutide is a trusted, effective weight-loss medication that helps control appetite and cravings. 
              Ideal for those seeking consistent, sustainable results.
            </p>
            <p className="text-neutral-dark">
              <strong>Best for:</strong> Newcomers to medical weight loss or those who want steady, proven progress.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg animate-fadeInUp">
            <h3 className="text-xl font-semibold text-primary mb-0">Tirzepatide</h3>
            <p className="text-primary-dark mb-4">The same active ingredient as Zepbound/Mounjaro</p>
            <p className="text-neutral-dark mb-4">
              Tirzepatide is the next-generation option for more powerful results. 
              By targeting two key appetite hormones, it can deliver faster, more dramatic weight loss.
            </p>
            <p className="text-neutral-dark">
              <strong>Best for:</strong> Those ready to maximize fat loss or accelerate their journey with advanced therapy.
            </p>
          </div>
        </div>
        <p className="text-center text-neutral-dark mt-8 text-md">
          Need guidance? Our expert providers are ready to help you choose the perfect plan for your goals.
        </p>
      </Section>

      {/* Pricing Section */}
      <Section background="bg-secondary-light" width="fixed" className="py-16">
        <h2 className="text-4xl font-extrabold text-center text-primary mb-12">
          Choose Your Plan — Only Pay After You Qualify
        </h2>
        <p className="text-center text-secondary-dark mb-8 max-w-3xl mx-auto text-lg">
          Everything you need to succeed — doctor consultations, prescriptions, supplies, and real support — for less than your insurance copay.
          <br className="hidden md:block" /> 
          Doctor visits are always $25.00 when you need us. No subscriptions. No memberships. No surprises.
        </p>

        <div className="flex flex-col md:flex-row lg:flex-row gap-10">
          <PricingBox
            title="Semaglutide Plan"
            price={planPrice(semaglutidePrice)}
            features={[
              'Virtual Doctor Consultation',
              'Semaglutide Prescription',
              'Medication & Supplies Included',
              'Online Refills Available',
              'Priority Email Support',
              '90-Day Progress Check-Ins',
            ]}
	            buttonText="View Full Pricing"
	            buttonLink="/pricing"
            buttonClass="bg-button hover:bg-button-light text-white"
          />
          <PricingBox
            title="Tirzepatide Plan"
            price={planPrice(tirzepatidePrice)}
            features={[
              'Virtual Doctor Consultation',
              'Tirzepatide Prescription',
              'Medication & Supplies Included',
              'Online Refills Available',
              'Priority Email Support',
              '90-Day Progress Check-Ins',
            ]}
	            buttonText="View Full Pricing"
	            buttonLink="/pricing"
            buttonClass="bg-button hover:bg-button-light text-white"
          />
        </div>

        <p className="text-center text-secondary-dark text-sm mt-8">
          * Some patients may require a basic lab panel.
        </p>
      </Section>

      {/* Closing CTA */}
      <Section background="bg-highlight-light" width="fixed" className="py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl font-extrabold text-primary mb-6">
            Start Your Journey — Real Doctors. Real Support. Real Results.
          </h2>
          <p className="text-neutral-dark text-lg mb-8">
            Join thousands who have changed their lives with RediMD.  
            No memberships. No gimmicks. No hidden costs.
            <br className="hidden md:block" /> 
            Just real healthcare, built around you.
          </p>
          <Link
            to="/register"
            className="bg-button hover:bg-button-light text-white font-semibold px-8 py-4 rounded-md text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg"
          >
            Begin My Transformation
          </Link>
          <p className="text-sm text-neutral-dark mt-4">
            Only pay after you qualify, then every doctor visit is $25.00. We make it easy to start, stay motivated, and succeed.
          </p>
        </div>
      </Section>


      {/* Footer with Contact Information */}
      <Section className="py-16 text-white" background="bg-primary">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:space-x-10 space-y-8 md:space-y-0 flex-nowrap w-full">
            
            {/* Company Info */}
            <div className="flex-[2]">
              <h3 className="text-xl font-bold mb-4 text-highlight">About RediMD</h3>
              <p className="mb-4">
                Leading telemedicine provider since 2008, offering affordable weight management solutions with real doctors and personalized care.
              </p>
            </div>

            {/* Contact Information */}
            <div className="flex-[1.2]">
              <h3 className="text-xl font-bold mb-4 text-highlight">Contact Us</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <FontAwesomeIcon icon={faPhone} className="mr-3 text-highlight" />
                  <span>(866)989-2873</span>
                </li>
                <li className="flex items-center">
                  <FontAwesomeIcon icon={faEnvelope} className="mr-3 text-highlight" />
                  <span>redimdsupport@redimd.com</span>
                </li>
                <li className="flex items-center">
                  <FontAwesomeIcon icon={faClock} className="mr-3 text-highlight" />
                  <span>Available online 24/7.</span>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div className="flex-[1]">
              <h3 className="text-xl font-bold mb-4 text-highlight ">Quick Links</h3>
              <ul className="space-y-2">
                {/* <li><a href="/about" className="hover:text-highlight transition-colors">About Us</a></li>
                <li><a href="/faq" className="hover:text-highlight transition-colors">FAQs</a></li> */}
                <li><a href="#testimonials" className="hover:text-highlight transition-colors">Success Stories</a></li>
              </ul>
            </div>
          </div>
        </div>
      </Section>


      {/* Bottom Footer */}
      <Section background="bg-white" className="border-t border-gray-700 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
        <p>&copy; {new Date().getFullYear()} RediMD. All rights reserved.</p>
        <div className="mt-4 md:mt-0">
          <Link to="/privacy" className="mr-6 hover:text-secondary-light transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-secondary-light transition-colors">Terms of Service</Link>
        </div>
      </Section>

    </div>
  );
}

export default LandingPage;
